import { CANARY, STABLE, toRolloutGroup, type RolloutGroup } from '@/shared/lib/constants/rolloutGroup';

const COOKIE_NAME = 'rollout_group';

/** Superseded by COOKIE_NAME. Expired once at module eval so it cannot linger as a routing input. */
const LEGACY_COOKIE_NAME = 'portal_version';

/** Dismissal is scoped to a `<relyingPartyId>:<group>` pair, not a bare flag -- see below. */
const DISMISSED_KEY = 'th_rolloutGroupPromptDismissed';

/** Stores the group the user pinned themselves to, not a boolean -- see isPinnedToStable. */
const OVERRIDE_KEY = 'th_rolloutGroupOverride';

/** One-shot marker for "just logged in or switched company" -- see markEnteredApp. */
const ENTERED_APP_KEY = 'th_rolloutEnteredApp';

const readCookie = (name: string): string | undefined =>
  document.cookie
    .split('; ')
    .find(row => row.startsWith(`${name}=`))
    ?.split('=')[1];

const deleteCookie = (name: string): void => {
  // Attributes must match how it was written (path=/, no Domain) or the browser keeps it.
  document.cookie = `${name}=; path=/; max-age=0`;
};

export const getRolloutGroupCookie = (): RolloutGroup => toRolloutGroup(readCookie(COOKIE_NAME));

export const setRolloutGroupCookie = (group: RolloutGroup): void => {
  if (getRolloutGroupCookie() === group) return;

  if (group === CANARY) {
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_NAME}=${CANARY}; path=/; SameSite=Lax${secure}`;
  } else {
    deleteCookie(COOKIE_NAME);
  }
};

/**
 * The cookie as it stood when this document was requested -- i.e. exactly what the load balancer
 * used to choose a target group. This is how the app knows which build it is running now that
 * there is no build-time constant.
 *
 * The capture is trustworthy because it lives in the same module as `setRolloutGroupCookie`, the
 * only writer: a module's dependencies finish evaluating before the importer's body runs, so no
 * code path can write the cookie before this line. Moving it into the constants module would
 * break that guarantee. It is an inference rather than a fact -- if the load balancer rule is
 * missing or misconfigured this says canary while stable was served -- but it fails towards the
 * safe build, since an absent cookie normalises to STABLE.
 */
export const SERVED_ROLLOUT_GROUP: RolloutGroup = getRolloutGroupCookie();

deleteCookie(LEGACY_COOKIE_NAME);

/**
 * Marks that the app was just entered by logging in or switching company -- the only two moments
 * where a mismatch is allowed to reload the page without asking. Set at those call sites; the sync
 * hook consumes it.
 *
 * One-shot, and consumed *before* the reload is issued. That is what makes it loop-safe: if the
 * reload does not actually change which build is served (no load balancer locally, or a
 * misconfigured rule), the flag is already gone, so it reloads at most once per entry instead of
 * forever.
 */
export const markEnteredApp = (): void => {
  sessionStorage.setItem(ENTERED_APP_KEY, '1');

  // Entering the app also ends any personal override. The escape hatch is meant to last until the
  // user is done with the session that went wrong -- not to follow them into the next login. It
  // has to be cleared here rather than on logout, because the override survives an abandoned
  // session, and once it is set the cookie reads stable, which hides the very chip that set it --
  // leaving no way back to canary short of closing the tab.
  sessionStorage.removeItem(OVERRIDE_KEY);
};

export const consumeEnteredApp = (): boolean => {
  const entered = sessionStorage.getItem(ENTERED_APP_KEY) === '1';
  if (entered) sessionStorage.removeItem(ENTERED_APP_KEY);
  return entered;
};

/**
 * Dismissal is keyed by pharmacy and target group rather than being a single session flag.
 * A bare flag meant that dismissing "you've been enrolled" in the morning silenced a rollback
 * in the afternoon, and that switching companies reused the previous pharmacy's decision.
 */
const dismissalKey = (relyingPartyId: string, group: RolloutGroup) => `${relyingPartyId}:${group}`;

export const wasPromptDismissed = (relyingPartyId: string, group: RolloutGroup): boolean =>
  sessionStorage.getItem(DISMISSED_KEY) === dismissalKey(relyingPartyId, group);

export const dismissPrompt = (relyingPartyId: string, group: RolloutGroup): void =>
  sessionStorage.setItem(DISMISSED_KEY, dismissalKey(relyingPartyId, group));

/**
 * The personal escape hatch only ever drops someone back to the stable build, so the guard is
 * directional. A symmetric "an override is active" boolean would also stop the sync effect from
 * clearing a canary cookie, pinning that user to canary through a rollback -- the one failure this
 * whole mechanism exists to prevent. Any value other than STABLE falls through to normal syncing.
 */
export const isPinnedToStable = (): boolean => sessionStorage.getItem(OVERRIDE_KEY) === STABLE;

export const pinToStable = (): void => {
  sessionStorage.setItem(OVERRIDE_KEY, STABLE);
  setRolloutGroupCookie(STABLE);
};
